import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { logout } from '../../store/actions/user.actions';
import { QRCodeModal } from '../profile/QRCodeModal';
import { ProfileModal } from '../profile/ProfileModal';
import { InstallButton } from '../pwa/InstallButton';
import authService from '../../services/auth';
import { User } from '../../types';
import { useBranding } from '../../hooks/useBranding';
import { NotificationService } from '../../utils/notifications';
import { getAvatarColors } from '../../utils/avatarColors';
// import { getNewMemberCount } from '../../api/userApi'; // Backend API not yet implemented

export const AppBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loginUser: user } = useAppSelector((state) => state.user);
  const { conversations } = useAppSelector((state) => state.messages);
  const [menuOpen, setMenuOpen] = useState(false); // Avatar menu (profile/settings)
  const [navMenuOpen, setNavMenuOpen] = useState(false); // Mobile nav menu
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [detailedUser, setDetailedUser] = useState<User | null>(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => NotificationService.isEnabled());
  const notificationsBlocked = 'Notification' in window && Notification.permission === 'denied';

  useEffect(() => {
    setNotificationsEnabled(NotificationService.isEnabled());
  }, [menuOpen]);

  const handleToggleNotifications = async () => {
    if (notificationsBlocked) {
      alert('Notifications are blocked by your browser. To enable them, open your browser site settings and allow notifications for this site.');
      return;
    }
    if (notificationsEnabled) {
      await NotificationService.disable();
      setNotificationsEnabled(false);
    } else {
      const granted = await NotificationService.enable();
      setNotificationsEnabled(granted);
    }
  };

  const branding = useBranding();

  const userName = user?.displayName || user?.display_name || user?.firstName || user?.first_name || user?.email || '?';
  const userInitial = userName.charAt(0).toUpperCase();
  const avatarColors = getAvatarColors(userName, user?.avatar_color);

  // Check if user is admin (ID 1) or manager (ID 2)
  const userId = String(user?.id || '');
  const isAdminOrManager = userId === '1' || userId === '2';

  // Calculate total unread messages
  const totalUnreadMessages = conversations.reduce((total, conv) => total + conv.unreadCount, 0);

  // Load new member count for admin/manager - Backend API pending
  /*
  useEffect(() => {
    const loadNewMemberCount = async () => {
      if (!isAdminOrManager || !user) return;

      try {
        const response = await getNewMemberCount();
        if (response.data?.success && response.data.data) {
          setNewMemberCount(response.data.data.count || 0);
        }
      } catch (error) {
        console.error('Failed to load new member count:', error);
      }
    };

    loadNewMemberCount();

    // Refresh count every 5 minutes
    const interval = setInterval(loadNewMemberCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAdminOrManager, user]);
  */

  const handleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleNavMenu = () => {
    setNavMenuOpen(!navMenuOpen);
  };

  const handleClose = () => {
    setMenuOpen(false);
  };

  const handleNavClose = () => {
    setNavMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      setMenuOpen(false);
      await dispatch(logout());
      // Redirect to auth page after successful logout
      navigate('/auth');
    } catch (error) {
      console.error('Logout failed:', error);
      setMenuOpen(false);
    }
  };

  const handleNavigateToDirectory = () => {
    handleNavClose();
    navigate('/dashboard/directory');
  };

  const handleNavigateToMap = () => {
    handleNavClose();
    navigate('/dashboard/map');
  };

  const handleNavigateToCalendar = () => {
    handleNavClose();
    navigate('/dashboard/calendar');
  };

  const handleNavigateToHome = () => {
    handleNavClose();
    navigate(branding.showPosts ? '/dashboard/home' : '/dashboard/messages');
  };

  const handleNavigateToMessages = () => {
    handleNavClose();
    navigate('/dashboard/messages');
  };

  const handleNavigateToProfile = async () => {
    handleClose();
    if (!user) return;

    try {
      // Fetch the user's full profile data
      const profileData = await authService.getUserProfile(user.id);
      setDetailedUser(profileData);
      setProfileModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      // Fallback to using the user data we have
      setDetailedUser(user);
      setProfileModalOpen(true);
    }
  };

  const handleNavigateToSettings = () => {
    handleClose();
    handleNavClose();
    navigate('/dashboard/settings');
  };

  return (
    <header className="appbar appbar-sticky">
      <div className="toolbar">
        <div className="flex flex-align-center flex-grow-1">
          {/* Mobile: avatar/hamburger toggle */}
          <button
            className="icon-btn appbar-nav-mobile"
            onClick={handleNavMenu}
            style={{ marginRight: '8px', color: 'inherit' }}
            aria-label="Menu"
          >
            {navMenuOpen ? (
              <span className="material-icons">close</span>
            ) : (user?.photoURL || user?.photo_url) ? (
              <div className="avatar" style={{ width: 32, height: 32 }}>
                <img src={user.photoURL || user.photo_url} alt="Menu" />
              </div>
            ) : (
              <div className="avatar" style={{ width: 32, height: 32, backgroundColor: avatarColors.backgroundColor, color: avatarColors.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 600, borderRadius: '50%' }}>
                {userInitial}
              </div>
            )}
          </button>

          {/* Brand block — hidden when white-label toggle is off */}
          {branding.showBlock && (
            <div
              className="flex flex-align-center"
              onClick={handleNavigateToHome}
              style={{ cursor: 'pointer', gap: '8px' }}
            >
              <img
                src={branding.logoUrl || '/logo192.png'}
                alt="Logo"
                style={{ height: 32 }}
                className="appbar-nav-desktop"
              />
              <div style={{ fontSize: '20px', fontWeight: 'bold', fontFamily: 'inherit' }}>
                <span style={{ color: '#3a9397' }}>{branding.part1}</span>
                <span style={{ color: '#ff9800' }}>{branding.part2}</span>
              </div>
            </div>
          )}
        </div>

        {/* Desktop Navigation - Hidden on mobile */}
        <div className="appbar-nav-desktop">
          {branding.showPosts && (
            <button
              className="icon-btn mr-1"
              onClick={handleNavigateToHome}
              title="Home"
              style={{ color: 'inherit' }}
            >
              <span className="material-icons">home</span>
            </button>
          )}

          <button
            className="icon-btn mr-1"
            onClick={handleNavigateToDirectory}
            title="Family Directory"
            style={{ color: 'inherit' }}
          >
            <span className="material-icons">people</span>
          </button>

          {branding.showMap && (
            <button
              className="icon-btn mr-1"
              onClick={handleNavigateToMap}
              title="Family Map"
              style={{ color: 'inherit' }}
            >
              <span className="material-icons">map</span>
            </button>
          )}

          {branding.showBirthday && (
            <button
              className="icon-btn mr-1"
              onClick={handleNavigateToCalendar}
              title="Birthday Calendar"
              style={{ color: 'inherit' }}
            >
              <span className="material-icons">event</span>
            </button>
          )}

          <button
            className="icon-btn mr-1"
            onClick={handleNavigateToMessages}
            title="Messages"
            style={{ color: 'inherit', position: 'relative' }}
          >
            <span className="material-icons">chat_bubble</span>
            {totalUnreadMessages > 0 && (
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                backgroundColor: '#f44336',
                color: 'white',
                borderRadius: '50%',
                minWidth: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: 'bold',
                padding: '0 4px',
                border: '2px solid var(--primary-dark)'
              }}>
                {totalUnreadMessages > 99 ? '99+' : totalUnreadMessages}
              </span>
            )}
          </button>

          {branding.showQR && (
            <button
              className="icon-btn mr-1"
              onClick={() => setQrModalOpen(true)}
              title="My QR Code"
              style={{ color: 'inherit' }}
            >
              <span className="material-icons">qr_code</span>
            </button>
          )}

          <div style={{ marginRight: '8px' }}>
            <InstallButton />
          </div>

          <div className="position-relative">
            <button
              className="icon-btn"
              onClick={handleMenu}
              style={{ color: 'inherit' }}
              aria-label="account of current user"
            >
              {user?.photoURL || user?.photo_url ? (
                <div className="avatar" style={{ width: 32, height: 32 }}>
                  <img src={user?.photoURL || user?.photo_url} alt="Profile" />
                </div>
              ) : (
                <div className="avatar" style={{ width: 32, height: 32, backgroundColor: avatarColors.backgroundColor, color: avatarColors.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 600, borderRadius: '50%' }}>
                  {userInitial}
                </div>
              )}
            </button>

            {menuOpen && (
              <>
                <div
                  className="position-fixed"
                  style={{
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    zIndex: 1300,
                    backgroundColor: 'transparent'
                  }}
                  onClick={handleClose}
                ></div>
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  right: 0,
                  zIndex: 1350,
                  minWidth: '200px',
                  maxWidth: '280px',
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  boxShadow: '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
                  padding: '8px 0',
                  listStyle: 'none',
                  margin: '0',
                  overflow: 'auto'
                }}>
                  <div
                    onClick={handleNavigateToProfile}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderRadius: '0',
                      transition: 'background-color 0.15s',
                      minHeight: '48px',
                      textDecoration: 'none',
                      color: 'rgba(0, 0, 0, 0.87)',
                      whiteSpace: 'nowrap',
                      boxSizing: 'border-box'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span className="material-icons mr-2" style={{ fontSize: '20px' }}>account_circle</span>
                    <span>Profile</span>
                  </div>

                  <div
                    onClick={handleNavigateToSettings}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderRadius: '0',
                      transition: 'background-color 0.15s',
                      minHeight: '48px',
                      textDecoration: 'none',
                      color: 'rgba(0, 0, 0, 0.87)',
                      whiteSpace: 'nowrap',
                      boxSizing: 'border-box'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span className="material-icons mr-2" style={{ fontSize: '20px' }}>settings</span>
                    <span>Settings</span>
                  </div>

                  <div
                    onClick={handleToggleNotifications}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      cursor: notificationsBlocked ? 'default' : 'pointer',
                      borderRadius: '0',
                      transition: 'background-color 0.15s',
                      minHeight: '48px',
                      color: notificationsBlocked ? 'rgba(0,0,0,0.38)' : 'rgba(0, 0, 0, 0.87)',
                      whiteSpace: 'nowrap',
                      boxSizing: 'border-box'
                    }}
                    onMouseEnter={(e) => { if (!notificationsBlocked) e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'; }}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    title={notificationsBlocked ? 'Blocked in browser settings' : undefined}
                  >
                    <span className="material-icons mr-2" style={{ fontSize: '20px' }}>
                      {notificationsBlocked ? 'notifications_off' : notificationsEnabled ? 'notifications_active' : 'notifications_off'}
                    </span>
                    <span>{notificationsBlocked ? 'Notifications blocked' : notificationsEnabled ? 'Notifications on' : 'Notifications off'}</span>
                  </div>

                  <div style={{ margin: '8px 0', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}></div>

                  <div
                    onClick={handleLogout}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderRadius: '0',
                      transition: 'background-color 0.15s',
                      color: '#d32f2f',
                      minHeight: '48px',
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                      boxSizing: 'border-box'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span className="material-icons mr-2" style={{ fontSize: '20px' }}>logout</span>
                    <span>Logout</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Mobile Nav Menu - Left Side Drawer */}
        {navMenuOpen && (
          <>
            <div
              className="position-fixed appbar-nav-mobile"
              style={{
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 1300,
                backgroundColor: 'rgba(0, 0, 0, 0.5)'
              }}
              onClick={handleNavClose}
            ></div>
            <div className="mobile-nav-drawer">
              {branding.showPosts && (
                <div
                  onClick={handleNavigateToHome}
                  className="mobile-menu-item"
                >
                  <span className="material-icons mr-2">home</span>
                  <span>Home</span>
                </div>
              )}

              <div
                onClick={handleNavigateToDirectory}
                className="mobile-menu-item"
              >
                <span className="material-icons mr-2">people</span>
                <span>Family Directory</span>
              </div>

              {branding.showMap && (
                <div
                  onClick={handleNavigateToMap}
                  className="mobile-menu-item"
                >
                  <span className="material-icons mr-2">map</span>
                  <span>Family Map</span>
                </div>
              )}

              {branding.showBirthday && (
                <div
                  onClick={handleNavigateToCalendar}
                  className="mobile-menu-item"
                >
                  <span className="material-icons mr-2">event</span>
                  <span>Birthday Calendar</span>
                </div>
              )}

              <div
                onClick={handleNavigateToMessages}
                className="mobile-menu-item"
                style={{ position: 'relative' }}
              >
                <span className="material-icons mr-2">chat_bubble</span>
                <span>Messages</span>
                {totalUnreadMessages > 0 && (
                  <span style={{
                    position: 'absolute',
                    right: '16px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    borderRadius: '12px',
                    minWidth: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    padding: '0 6px'
                  }}>
                    {totalUnreadMessages > 99 ? '99+' : totalUnreadMessages}
                  </span>
                )}
              </div>

              <div style={{ margin: '8px 0', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}></div>

              {branding.showQR && (
                <div
                  onClick={() => {
                    handleNavClose();
                    setQrModalOpen(true);
                  }}
                  className="mobile-menu-item"
                >
                  <span className="material-icons mr-2">qr_code</span>
                  <span>My QR Code</span>
                </div>
              )}

              <div
                onClick={handleNavigateToProfile}
                className="mobile-menu-item"
              >
                <span className="material-icons mr-2">account_circle</span>
                <span>My Profile</span>
              </div>

              {isAdminOrManager && (
                <div
                  onClick={handleNavigateToSettings}
                  className="mobile-menu-item"
                >
                  <span className="material-icons mr-2">settings</span>
                  <span>Settings</span>
                </div>
              )}

              <div
                onClick={handleToggleNotifications}
                className="mobile-menu-item"
                style={{ color: notificationsBlocked ? 'rgba(0,0,0,0.38)' : undefined }}
              >
                <span className="material-icons mr-2">
                  {notificationsBlocked || !notificationsEnabled ? 'notifications_off' : 'notifications_active'}
                </span>
                <span>
                  {notificationsBlocked ? 'Notifications blocked' : notificationsEnabled ? 'Notifications on' : 'Notifications off'}
                </span>
              </div>

              <div style={{ margin: '8px 0', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}></div>

              <div
                onClick={handleLogout}
                className="mobile-menu-item"
                style={{ color: '#d32f2f' }}
              >
                <span className="material-icons mr-2">logout</span>
                <span>Logout</span>
              </div>
            </div>
          </>
        )}
      </div>

      <ProfileModal
        open={profileModalOpen}
        onClose={() => {
          setProfileModalOpen(false);
          setDetailedUser(null);
        }}
        user={detailedUser}
        currentUser={user}
        onEdit={() => {}}
        onContact={() => {}}
        onViewLocation={() => {}}
        onUpdate={(updatedUser) => setDetailedUser(updatedUser)}
      />

      <QRCodeModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
      />
    </header>
  );
};